import { addItemsToCart } from 'cart-add-helpers';

// must match the cart attribute condition of the discount app rule
const DISCOUNT_ATTRIBUTE = 'routineDiscount';
const DISCOUNT_ATTRIBUTE_VALUE = 'true';

function formatMoney(cents) {
	const amount = cents / 100;
	const currency = window.Shopify?.currency?.active || 'EUR';
	const locale = document.documentElement.lang || 'de-DE';
	return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

class RoutineProducts extends HTMLElement {
	connectedCallback() {
		this.init();
	}

	disconnectedCallback() {
		this.cleanup();
	}

	init() {
		this.abortController = new AbortController();
		const { signal } = this.abortController;

		this.ctaButton = this.querySelector('.routine-products__cta');
		this.discountThreshold = Number(this.dataset.discountThreshold || 0);
		this.discountAmount = Number(this.dataset.discountAmount || 0);

		this.querySelectorAll('[data-routine-select]').forEach((button) => {
			button.addEventListener('click', () => this.toggleSelection(button), { signal });
		});

		if (this.ctaButton) {
			this.ctaButton.addEventListener('click', () => this.handleBundleAdd(), { signal });
		}

		this.updateSummary();
	}

	cleanup() {
		this.abortController?.abort();
	}

	toggleSelection(button) {
		const card = button.closest('[data-routine-card]');
		const wasSelected = button.getAttribute('aria-pressed') === 'true';
		button.setAttribute('aria-pressed', String(!wasSelected));
		card.classList.toggle('is-deselected', wasSelected);
		this.updateSummary();
	}

	getCurrentCard() {
		return this.querySelector('[data-routine-card][data-current]');
	}

	hasIncludedCards() {
		return Boolean(this.querySelector('[data-routine-card][data-included]'));
	}

	getCurrentProductPrice() {
		const currentCard = this.getCurrentCard();
		if (currentCard) {
			return {
				price: Number(currentCard.dataset.price || 0),
				comparePrice: Number(currentCard.dataset.comparePrice || 0),
			};
		}

		if (this.hasIncludedCards()) {
			return {
				price: Number(this.dataset.currentPrice || 0),
				comparePrice: Number(this.dataset.currentComparePrice || 0),
			};
		}

		return null;
	}

	getSelectedOtherCards() {
		return [...this.querySelectorAll('[data-routine-card]:not([data-current]):not([data-included])')].filter(
			(card) => !card.classList.contains('is-deselected'),
		);
	}

	isDiscountQualified(productCount) {
		return this.discountThreshold > 0 && productCount >= this.discountThreshold;
	}

	getDiscountState(selectedCount) {
		return {
			isQualified: this.isDiscountQualified(selectedCount),
			remaining: Math.max(this.discountThreshold - selectedCount, 0),
			progress: Math.min(selectedCount, this.discountThreshold),
		};
	}

	updateDiscountStatus({ isQualified, remaining, progress }) {
		const status = this.querySelector('.routine-discount-status');
		if (!status) return;

		const chip = status.querySelector('.routine-discount-status__chip');
		const text = status.querySelector('.routine-discount-status__text');

		const textToShow = isQualified ? status.dataset.successText : status.dataset.progressText;
		status.classList.toggle('routine-discount-status--success', isQualified);

		chip.textContent = isQualified ? status.dataset.successChip : `${progress}/${this.discountThreshold}`;
		text.textContent = textToShow.replace('%remaining%', remaining);
		status.hidden = false;
	}

	updateDiscountProgress({ isQualified, remaining, progress }) {
		const progressBar = this.querySelector('.routine-discount-progress-bar');
		if (!progressBar) return;

		const label = progressBar.querySelector('.routine-discount-progress-bar__label');
		const counter = progressBar.querySelector('.routine-discount-progress-bar__counter');
		const segments = progressBar.querySelectorAll('.routine-discount-progress-bar__segment');
		segments.forEach((segment, index) => {
			segment.classList.toggle('is-filled', index < progress);
		});

		let labelText = progressBar.dataset.remainingOtherText;
		if (isQualified) {
			labelText = progressBar.dataset.successText;
		} else if (remaining === 1) {
			labelText = progressBar.dataset.remainingOneText;
		}

		label.textContent = labelText.replace('%remaining%', remaining);
		counter.textContent = progressBar.dataset.counterText.replace('%selected%', progress);
		progressBar.classList.toggle('routine-discount-progress-bar--success', isQualified);
		progressBar.hidden = false;
	}

	updateSummary() {
		const currentPrice = this.getCurrentProductPrice();
		const selectedCards = this.getSelectedOtherCards();

		const prices = selectedCards.map((card) => ({
			price: Number(card.dataset.price || 0),
			comparePrice: Number(card.dataset.comparePrice || 0),
		}));

		if (currentPrice !== null) prices.unshift(currentPrice);

		const discountState = this.getDiscountState(prices.length);
		const subtotal = prices.reduce((sum, item) => sum + item.price, 0);
		const discount = discountState.isQualified ? Math.min(this.discountAmount, subtotal) : 0;
		const total = subtotal - discount;
		const compareTotal = prices.reduce((sum, item) => {
			return sum + Math.max(item.comparePrice, item.price);
		}, 0);

		const compareEl = this.querySelector('.routine-products__cta-compare-price');
		const priceEl = this.querySelector('.routine-products__cta-price');
		const countEl = this.querySelector('.routine-products__cta-count');
		if (compareEl) {
			if (compareTotal > total) {
				compareEl.textContent = formatMoney(compareTotal);
				compareEl.hidden = false;
			} else {
				compareEl.hidden = true;
			}
		}
		if (priceEl) {
			priceEl.textContent = formatMoney(total);
		}
		if (countEl) {
			countEl.textContent = prices.length ? `(${prices.length})` : '';
		}
		if (this.ctaButton) {
			this.ctaButton.disabled = prices.length === 0;
		}
		this.updateDiscountStatus(discountState);
		this.updateDiscountProgress(discountState);
	}

	buildSelectedItems() {
		const currentCard = this.getCurrentCard();
		const items = [];

		if (currentCard || this.hasIncludedCards()) {
			items.push({
				id: Number(this.dataset.currentVariantId),
				quantity: 1,
				properties: { _routine_id: this.id, _routine_role: 'current' },
			});
		}

		this.getSelectedOtherCards().forEach((card) => {
			items.push({
				id: Number(card.dataset.variantId),
				quantity: 1,
				properties: { _routine_id: this.id, _routine_role: 'child' },
			});
		});

		return items;
	}

	async handleBundleAdd() {
		const items = this.buildSelectedItems();
		if (!items.length) {
			return;
		}

		this.ctaButton.setAttribute('aria-busy', 'true');

		try {
			await this.prepareCart();
			await addItemsToCart(items);
		} catch (error) {
			console.error('routine-products bundle add failed:', error);
			document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: error.message, tone: 'error' } }));
		} finally {
			this.ctaButton.removeAttribute('aria-busy');
		}
	}

	// attribute is set before the add so the drawer refresh after the add already shows the discount
	async prepareCart() {
		const cart = await this.fetchCart();
		const body = {};
		const updates = this.getCurrentVariantRemovals(cart);
		if (Object.keys(updates).length) {
			body.updates = updates;
		}
		if (this.isDiscountQualified(this.countRoutineProducts(cart))) {
			body.attributes = { [DISCOUNT_ATTRIBUTE]: DISCOUNT_ATTRIBUTE_VALUE };
		}
		if (!Object.keys(body).length) {
			return;
		}

		const updateResponse = await fetch(`${Shopify.routes.root}cart/update.js`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
		if (!updateResponse.ok) {
			throw new Error(`HTTP error: ${updateResponse.status}`);
		}
	}

	async fetchCart() {
		const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
			headers: { Accept: 'application/json' },
		});
		if (!cartResponse.ok) {
			throw new Error(`HTTP error: ${cartResponse.status}`);
		}
		return cartResponse.json();
	}

	// routine products from earlier routine adds count too, so adding 2 and then 1 more still unlocks the discount
	countRoutineProducts(cart) {
		const productIdsInCart = cart.items.filter((item) => item.properties?._routine_id).map((item) => item.product_id);
		const currentCard = this.getCurrentCard();
		//const selectedCards = currentCard ? [currentCard, ...this.getSelectedOtherCards()] : this.getSelectedOtherCards();
		const selectedProductIds = this.getSelectedOtherCards().map((card) => Number(card.dataset.productId));

		if (currentCard || this.hasIncludedCards()) {
			selectedProductIds.push(Number(this.dataset.currentProductId));
		}

		return new Set([...productIdsInCart, ...selectedProductIds]).size;
	}

	getCurrentVariantRemovals(cart) {
		const updates = {};
		const currentVariantId = Number(this.dataset.currentVariantId);
		if (!currentVariantId) {
			return updates;
		}

		cart.items
			.filter((item) => item.variant_id === currentVariantId)
			.forEach((line) => {
				updates[line.key] = 0;
			});

		return updates;
	}
}

if (!window.customElements.get('routine-products')) {
	window.customElements.define('routine-products', RoutineProducts);
}
